// File: webelieve-frontend/src/features/notes/components/NoteInviteCollaboratorModal.tsx
// Invite modal for note collaboration (internal user picker)

import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../../api/axios';

interface InviteCandidate {
  user_id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  role?: string | null;
  display_name?: string | null;
}

interface NoteInviteCollaboratorModalProps {
  isOpen: boolean;
  noteId?: string | null;
  noteTitle?: string;
  onClose: () => void;
  onInvite: (payload: {
    user_id: string;
    role: 'editor' | 'reviewer' | 'viewer';
    message?: string;
    expires_in_hours?: number;
  }) => Promise<void>;
}

const DEFAULT_EXPIRY_HOURS = 168;

const NoteInviteCollaboratorModal: React.FC<NoteInviteCollaboratorModalProps> = ({
  isOpen,
  noteId,
  noteTitle,
  onClose,
  onInvite,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [candidates, setCandidates] = useState<InviteCandidate[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<InviteCandidate | null>(null);
  const [role, setRole] = useState<'editor' | 'reviewer' | 'viewer'>('viewer');
  const [message, setMessage] = useState('');
  const [expiresInHours, setExpiresInHours] = useState<number>(DEFAULT_EXPIRY_HOURS);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const candidateRequestIdRef = useRef(0);

  const fetchCandidates = useCallback(async (query: string) => {
    if (!noteId) return;
    const requestId = ++candidateRequestIdRef.current;

    setLoadingCandidates(candidates.length === 0);
    setIsSearching(candidates.length > 0);
    setCandidateError(null);

    try {
      const response = await apiClient.get(`/notes/${noteId}/invitees`, {
        params: {
          search: query.trim() || undefined,
          limit: 50,
        },
      });

      if (requestId !== candidateRequestIdRef.current) return;
      const users = response?.data?.data?.users || [];
      setCandidates(users);

      if (selectedUserId) {
        const matched = users.find((user: InviteCandidate) => user.user_id === selectedUserId);
        if (matched) {
          setSelectedCandidate(matched);
        }
      }
    } catch (listError: any) {
      if (requestId !== candidateRequestIdRef.current) return;
      setCandidates([]);
      setCandidateError(
        listError?.response?.data?.message ||
        listError?.message ||
        'Failed to load users for invitation.'
      );
    } finally {
      if (requestId !== candidateRequestIdRef.current) return;
      setLoadingCandidates(false);
      setIsSearching(false);
    }
  }, [noteId, selectedUserId, candidates.length]);

  useEffect(() => {
    if (!isOpen) return;

    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCandidates([]);
    setSelectedUserId('');
    setSelectedCandidate(null);
    setRole('viewer');
    setMessage('');
    setExpiresInHours(DEFAULT_EXPIRY_HOURS);
    setLoadingCandidates(false);
    setCandidateError(null);
    setSubmitting(false);
    setError(null);
    setIsSearching(false);
    candidateRequestIdRef.current += 1;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timerId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 320);
    return () => window.clearTimeout(timerId);
  }, [isOpen, searchTerm]);

  useEffect(() => {
    if (!isOpen || !noteId) return;
    const timerId = window.setTimeout(() => {
      void fetchCandidates(debouncedSearchTerm);
    }, 0);
    return () => window.clearTimeout(timerId);
  }, [fetchCandidates, isOpen, noteId, debouncedSearchTerm]);

  if (!isOpen) {
    return null;
  }

  const getCandidateName = (candidate: InviteCandidate): string => (
    candidate.display_name?.trim() ||
    `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() ||
    candidate.email ||
    candidate.user_id
  );

  const getCandidateSecondary = (candidate: InviteCandidate): string => (
    candidate.email ||
    candidate.role ||
    candidate.user_id
  );

  const handleCandidateSelect = (candidate: InviteCandidate) => {
    setSelectedUserId(candidate.user_id);
    setSelectedCandidate(candidate);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedUserId) {
      setError('Please select a collaborator from the user list.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onInvite({
        user_id: selectedUserId,
        role,
        message: message.trim() || undefined,
        expires_in_hours: expiresInHours,
      });
      onClose();
    } catch (inviteError: any) {
      setError(
        inviteError?.response?.data?.message ||
        inviteError?.message ||
        'Failed to send invitation. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Invite Collaborator</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {noteTitle ? `Share \"${noteTitle}\"` : 'Share this note'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            disabled={submitting}
            aria-label="Close invite dialog"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
          {(error || candidateError) && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2">
              {error || candidateError}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Find User</label>
              {isSearching && (
                <span className="text-[11px] text-gray-500 dark:text-gray-400">Searching...</span>
              )}
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by first name, last name, or email"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-lex-accent"
              disabled={submitting}
            />
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Invitations are limited to existing EdgeLex users in your firm.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Collaborator</label>
            <div className="w-full h-52 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 overflow-y-auto">
              {loadingCandidates ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Loading users...</div>
              ) : candidates.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching users found</div>
              ) : (
                candidates.map((candidate) => {
                  const isSelected = selectedUserId === candidate.user_id;
                  return (
                    <button
                      key={candidate.user_id}
                      type="button"
                      onClick={() => handleCandidateSelect(candidate)}
                      disabled={submitting}
                      className={`w-full text-left px-3 py-2 border-b last:border-b-0 border-gray-100 dark:border-gray-600 transition-colors ${
                        isSelected
                          ? 'bg-lex-accent-soft'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-600/30'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-medium ${isSelected ? 'text-lex-accent' : 'text-gray-900 dark:text-gray-100'}`}>
                          {getCandidateName(candidate)}
                        </span>
                        {isSelected && (
                          <span className="text-[11px] font-semibold text-lex-accent">Selected</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {getCandidateSecondary(candidate)}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            {selectedCandidate && (
              <div className="mt-2 rounded border border-lex-accent bg-lex-accent-soft px-2.5 py-2">
                <div className="text-[11px] text-lex-accent font-medium">Inviting</div>
                <div className="text-xs text-gray-800 dark:text-gray-100">{getCandidateName(selectedCandidate)}</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
              <select
                value={role}
                onChange={(event) => setRole(event.target.value as 'editor' | 'reviewer' | 'viewer')}
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-lex-accent"
                disabled={submitting}
              >
                <option value="editor">Editor</option>
                <option value="reviewer">Reviewer</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Expires In</label>
              <select
                value={expiresInHours}
                onChange={(event) => setExpiresInHours(Number(event.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-lex-accent"
                disabled={submitting}
              >
                <option value={24}>24 hours</option>
                <option value={72}>3 days</option>
                <option value={168}>7 days</option>
                <option value={336}>14 days</option>
                <option value={720}>30 days</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Message (optional)</label>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={3}
              placeholder="Add context for your collaborator"
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-lex-accent resize-none"
              disabled={submitting}
            />
          </div>

          <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 text-xs font-medium rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-1.5 text-xs font-medium rounded bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-600"
            >
              {submitting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NoteInviteCollaboratorModal;
